import { useCallback, useRef, useState } from 'react';

/**
 * Screenshot the user's screen into a `File` (ENG-4185).
 *
 * Ported from the Connect chat widget (`ConnectChatWidget.handleScreenshot`), which is
 * the only place in the product that has already done this in anger. The shape is the
 * same because the browser's rules are the same:
 *
 * * `getDisplayMedia` shows the native share picker on **every** call — there is no way
 *   to remember consent, and that is a deliberate platform decision, not a bug;
 * * the user clicking Cancel rejects with `NotAllowedError`;
 * * the video element needs a moment to paint before the canvas copy, or the capture is
 *   a blank frame.
 *
 * Two things this file learned the hard way:
 *
 * **Cancelling must say something.** Swallowing `NotAllowedError` left the button a
 * black hole: the picker dims the page, the user dismisses it, and *nothing* changes on
 * screen — indistinguishable from a broken feature. It is not an error, but it is not
 * silence either, so it comes back as a `notice`.
 *
 * **A pick can be dimension-less.** Some windows report `videoWidth === 0` on the first
 * frame; drawing that produces a 0×0 canvas and `toBlob` then yields a useless 1×1 PNG
 * that uploads happily and reads as "an empty screenshot". That is reported as an error
 * with the thing to try instead.
 */
const PAINT_DELAY_MS = 300;

export interface ScreenCapture {
  capture: () => Promise<File | null>;
  capturing: boolean;
  /** The attempt failed. Something to fix. */
  error: string | null;
  /** The attempt did not happen. Nothing to fix; say so quietly. */
  notice: string | null;
  clearError: () => void;
  clearNotice: () => void;
}

export const useScreenCapture = (): ScreenCapture => {
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const capturingRef = useRef(false);

  const capture = useCallback(async (): Promise<File | null> => {
    if (capturingRef.current) {
      return null;
    }
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices?.getDisplayMedia
    ) {
      setError(
        'Your browser cannot take a screenshot here. Attach an image instead.'
      );
      return null;
    }

    capturingRef.current = true;
    setCapturing(true);
    setError(null);
    setNotice(null);

    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true });

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      await video.play();
      await new Promise((resolve) => setTimeout(resolve, PAINT_DELAY_MS));

      const width = video.videoWidth;
      const height = video.videoHeight;
      if (!width || !height) {
        setError(
          'That window did not produce a picture. Try sharing a browser tab or the whole screen instead.'
        );
        return null;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        setError('That screenshot could not be taken. Try again.');
        return null;
      }
      context.drawImage(video, 0, 0, width, height);

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((result) => resolve(result), 'image/png');
      });
      if (!blob || blob.size === 0) {
        setError('That screenshot came back empty. Try again.');
        return null;
      }

      return new File([blob], `screenshot-${Date.now()}.png`, {
        type: 'image/png',
      });
    } catch (caught) {
      const name = (caught as Error)?.name ?? '';
      if (name === 'NotAllowedError' || name === 'AbortError') {
        // The share picker was opened and dismissed. Not a failure — but the user
        // pressed a button and got nothing, so say what happened.
        setNotice('No screen was shared, so nothing was attached.');
      } else if (name === 'NotReadableError') {
        setError(
          'The screen could not be read — another app may be blocking the capture. Try again.'
        );
      } else {
        setError('That screenshot could not be taken. Try again.');
      }
      return null;
    } finally {
      stream?.getTracks().forEach((track) => track.stop());
      capturingRef.current = false;
      setCapturing(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);
  const clearNotice = useCallback(() => setNotice(null), []);

  return { capture, capturing, error, notice, clearError, clearNotice };
};

export default useScreenCapture;
